// Admin layout: guard (VITE_ADMIN_EMAIL) + sidebar grouped (Overview, Catalog, Sales, Configuration)

import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator } from '@/components/ui/sidebar';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  FolderTree,
  Package,
  Layers,
  Users,
  FileText,
  LogOut,
  Tag,
  MapPin,
  Ship,
  Truck,
  Building2,
  Boxes,
  FileInput,
  AlertCircle,
  Landmark,
  Search,
} from 'lucide-react';
const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL ?? '';

function NavItem({
  to,
  icon: Icon,
  children,
  exact,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  exact?: boolean;
}) {
  const location = useLocation();
  const isActive = exact ? location.pathname === to : location.pathname.startsWith(to + (to.endsWith('/') ? '' : '/')) || location.pathname === to;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link to={to}>
          <Icon className="h-4 w-4 shrink-0" />
          {children}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

const AdminLayout = () => {
  const [user, setUser] = useState<{ email?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data?.session?.user ?? null);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/store/admin/login', { replace: true });
      return;
    }
    if (!loading && user && !isAdmin) {
      navigate('/store/admin/login', { replace: true });
    }
  }, [loading, user, isAdmin, navigate]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/store/admin/login');
  };

  if (loading || !user || !isAdmin) {
    return null;
  }

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarContent>
          {/* Inicio */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Inicio
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItem to="/store/admin" icon={LayoutDashboard} exact>
                  Dashboard
                </NavItem>
                <NavItem to="/store/admin/error-reports" icon={AlertCircle}>
                  Reportes de errores
                </NavItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Catálogo (estructura de la tienda) */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Catálogo
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItem to="/store/admin/categories" icon={FolderTree}>
                  Categorías
                </NavItem>
                <NavItem to="/store/admin/product-families" icon={Layers}>
                  Familias de producto
                </NavItem>
                <NavItem to="/store/admin/sourcing-items" icon={Package}>
                  Productos (sourcing)
                </NavItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Ventas y cotizaciones */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Ventas y cotizaciones
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItem to="/store/admin/leads" icon={Users}>
                  Leads
                </NavItem>
                <NavItem to="/store/admin/quotation-requests" icon={FileText}>
                  Solicitudes de cotización
                </NavItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Configuración (datos maestros) */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Configuración
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItem to="/store/admin/company-settings" icon={Landmark}>
                  Empresa y cuentas bancarias
                </NavItem>
                <NavItem to="/store/admin/brands" icon={Tag}>
                  Marcas
                </NavItem>
                <NavItem to="/store/admin/origins" icon={MapPin}>
                  Orígenes
                </NavItem>
                <NavItem to="/store/admin/origin-ports" icon={Ship}>
                  Puertos
                </NavItem>
                <NavItem to="/store/admin/incoterms" icon={Truck}>
                  Incoterms
                </NavItem>
                <NavItem to="/store/admin/suppliers" icon={Building2}>
                  Proveedores
                </NavItem>
                <NavItem to="/store/admin/loading-methods" icon={Boxes}>
                  Métodos de carga
                </NavItem>
                <NavItem to="/store/admin/pseo-config" icon={Search}>
                  pSEO Config
                </NavItem>
                <NavItem to="/store/admin/catalogue-converter" icon={FileInput}>
                  Conversor de catálogo
                </NavItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarSeparator />
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={signOut} className="text-muted-foreground hover:text-foreground">
                <LogOut className="h-4 w-4 shrink-0" />
                Cerrar sesión
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default AdminLayout;
